// Tiny HTML sanitizer + rich-text helpers for the inline editor. We keep the
// allow-list intentionally small (bold/italic/underline/link only) because
// landing-page copy rarely needs more, and a narrow surface means fewer XSS
// vectors to worry about. Any tag / attribute outside the allow-list is
// removed on save.
//
// Design notes
// ------------
// • We never trust the browser's contentEditable output as-is; every commit
//   flows through `sanitizeRichHtml()`.
// • On render, callers pass the stored HTML through `renderableRichHtml()`
//   which is idempotent — plain text (no HTML) gets escaped so `<script>`
//   never sneaks in through legacy plain fields.

const ALLOWED_TAGS = new Set(["b", "strong", "i", "em", "u", "a", "br"]);
const ALLOWED_ATTRS = { a: new Set(["href", "target", "rel"]) };

function _escape(s) {
  return String(s).replace(/[&<>"']/g, (c) => ({
    "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;",
  }[c]));
}

function _sanitizeNode(node) {
  const out = document.createDocumentFragment();
  node.childNodes.forEach((child) => {
    if (child.nodeType === Node.TEXT_NODE) {
      out.appendChild(document.createTextNode(child.textContent));
      return;
    }
    if (child.nodeType !== Node.ELEMENT_NODE) return;
    const tag = child.tagName.toLowerCase();
    if (!ALLOWED_TAGS.has(tag)) {
      // Strip disallowed wrapper but keep its (recursively sanitized) children
      out.appendChild(_sanitizeNode(child));
      return;
    }
    const el = document.createElement(tag);
    const allowedForTag = ALLOWED_ATTRS[tag] || new Set();
    for (const { name, value } of Array.from(child.attributes || [])) {
      if (!allowedForTag.has(name)) continue;
      // Reject dangerous protocols even on allowed href
      if (name === "href") {
        const trimmed = String(value || "").trim();
        if (/^\s*javascript:/i.test(trimmed) || /^\s*data:/i.test(trimmed)) continue;
      }
      el.setAttribute(name, value);
    }
    // Anchors: force safe defaults so target=_blank never leaks the opener
    if (tag === "a" && el.getAttribute("target") === "_blank") {
      el.setAttribute("rel", "noopener noreferrer");
    }
    el.appendChild(_sanitizeNode(child));
    out.appendChild(el);
  });
  return out;
}

/**
 * Sanitize arbitrary HTML down to the allow-list. Returns a safe HTML string.
 * Safe to pass user input straight from contentEditable.innerHTML.
 */
export function sanitizeRichHtml(html) {
  if (typeof window === "undefined") return "";
  if (!html) return "";
  const template = document.createElement("template");
  template.innerHTML = String(html);
  const frag = _sanitizeNode(template.content);
  const container = document.createElement("div");
  container.appendChild(frag);
  return container.innerHTML.trim();
}

/**
 * Return an HTML string safe to feed into `dangerouslySetInnerHTML`.
 * Handles legacy plain-text values (auto-escapes) and freshly-sanitised HTML.
 */
export function renderableRichHtml(value) {
  if (!value) return "";
  const str = String(value);
  // If the value looks like it contains HTML markup, we already sanitised on
  // save. If not, it's plain text → escape defensively so nothing dodgy renders.
  if (/<[a-z][^>]*>/i.test(str)) return str;
  return _escape(str);
}

/** True when the value already contains HTML tags (used by editors to decide). */
export function isRichHtml(value) {
  return /<[a-z][^>]*>/i.test(String(value || ""));
}
